import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import { useGetPhoneVerificationStatusQuery } from 'store/services/auth/authApi';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import { AppDispatch } from 'store';
import './UnverifiedPhoneBanner.css';

const UnverifiedPhoneBanner: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: userData } = useGetMeQuery();
  const { data: phoneStatus } = useGetPhoneVerificationStatusQuery();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);

  // Check if banner was previously dismissed (stored in localStorage)
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('phoneVerificationBannerDismissed');
    if (dismissedUntil) {
      const dismissedTime = parseInt(dismissedUntil, 10);
      const now = Date.now();
      // Keep banner dismissed for 24 hours
      if (now < dismissedTime) {
        setIsDismissed(true);
      } else {
        // Clear expired dismissal
        localStorage.removeItem('phoneVerificationBannerDismissed');
      }
    }
  }, []);

  // Don't show banner if:
  // - User data is not loaded yet
  // - Phone is already verified
  // - Banner was dismissed
  if (!userData || userData.phoneVerified || phoneStatus?.phoneVerified || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    // Dismiss for 24 hours
    const dismissUntil = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('phoneVerificationBannerDismissed', dismissUntil.toString());
    setIsDismissed(true);
  };

  // Verification happens in place. The banner hides itself once the status
  // query flips, so there is nothing to do here but refresh /users/me for
  // every other phoneVerified consumer.
  const handleVerified = () => {
    dispatch(meApi.util.invalidateTags(['User']));
  };

  return (
    <>
    <div className="unverified-phone-banner">
      <div className="banner-content">
        <div className="banner-icon">
          <img
            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/warning.svg"
            alt="Warning"
            width="20"
            height="20"
            style={{ filter: 'brightness(0) saturate(100%) invert(62%) sepia(98%) saturate(1247%) hue-rotate(6deg) brightness(102%) contrast(95%)' }}
          />
        </div>
        <span className="banner-message">
          <span className="banner-message-line">
            Verify your phone to unlock trading, SMS alerts and up to 1,600 bonus points.
          </span>
          <span className="banner-message-secondary">
            Verifying unlocks your Home checklist, where your first 100 points are ready to claim.
          </span>
        </span>
        <button
          className="banner-verify-button"
          onClick={() => setIsVerifyOpen(true)}
        >
          Verify phone
        </button>
      </div>
      <button
        className="banner-dismiss-button"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
        </svg>
      </button>
    </div>

    <MobileVerificationModal
      isOpen={isVerifyOpen}
      onClose={() => setIsVerifyOpen(false)}
      currentPhone={null}
      onVerificationSuccess={handleVerified}
    />
    </>
  );
};

export default UnverifiedPhoneBanner;